<template>
    <div class="history-panel" ref="historyPanel">
        <div class="history-filters">
            <label class="history-filter-field history-filter-search">
                <span>Search text</span>
                <input v-model="historySearchQuery" type="search" placeholder="Search generated text" />
            </label>
            <label class="history-filter-field history-filter-time">
                <span>Time</span>
                <select v-model="selectedTimeRange">
                    <option v-for="option in timeRangeOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
            </label>
            <button v-if="hasActiveFilters" class="clear-filter-button" type="button" @click="clearFilters">
                Clear
            </button>
        </div>
        <div class="history-results-bar">
            <span>{{ filteredHistoryList.length }} result{{ filteredHistoryList.length === 1 ? '' : 's' }}</span>
            <span v-if="historyList.length">Newest first</span>
        </div>
        <div class="table-container" ref="tableContainer">
            <table v-if="paginatedHistoryRows.length">
                <tbody>
                    <tr v-for="row in paginatedHistoryRows" :key="row.rowKey">
                        <td>
                            <button class="play-button" :class="{ active: isPlaying(row.rowKey) }"
                                @click="playAudio(row.item, row.rowKey)" :disabled="isPlayDisabled(row.rowKey)"
                                :aria-label="playButtonLabel(row.rowKey)">
                                <span v-if="isPlayLoading(row.rowKey)" class="button-spinner"
                                    aria-hidden="true"></span>
                                <span v-else-if="isPlaying(row.rowKey)" aria-hidden="true">■</span>
                                <span v-else aria-hidden="true">▶</span>
                            </button>
                        </td>
                        <td class="history-text-cell">
                            <span :title="payloadText(row.item.payload)">
                                {{ truncateText(payloadText(row.item.payload), 24) }}
                            </span>
                        </td>
                        <td :title="formatHistoryTime(row.item)">{{ timeAgo(row.item) }}</td>
                        <td>
                            <button class="download-button"
                                @click="downloadAudio(payloadText(row.item.payload), row.item, row.rowKey)"
                                :disabled="isDownloadDisabled(row.rowKey)"
                                :aria-label="`Download ${payloadText(row.item.payload) || 'audio'}`">
                                <span v-if="isDownloadLoading(row.rowKey)" class="button-spinner"
                                    aria-hidden="true"></span>
                                <span v-else aria-hidden="true">⬇</span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="history-empty">
                {{ emptyHistoryMessage }}
            </div>
        </div>
        <div class="pagination" ref="paginationBar" v-if="filteredHistoryList.length">
            <button @click="prevPage" :disabled="currentPage === 1">Prev</button>
            <span class="page-info">Page {{ currentPage }} / {{ totalPages }}</span>
            <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
        </div>
    </div>
</template>
<script>

import { getTTSRequest } from '@/api/services';
import { downloadFile, getDownloadFileUrl } from '@/api/files';
import { onMounted, onBeforeUnmount, nextTick, ref, computed, watch } from 'vue';
export default {
    name: 'HistoryPanel',
    props: {
        generatedAudio: {
            type: String,
            required: true
        },
    },
    setup(_props, { _emit }) {
        const historyList = ref([]);
        const historySearchQuery = ref('');
        const selectedTimeRange = ref('all');
        const loadingPlayKey = ref(null);
        const playingAudioKey = ref(null);
        const loadingDownloadKey = ref(null);
        let activeAudio = null;
        let activeAudioUrl = '';
        let playRequestId = 0;
        let observer = null;

        const sortedHistoryList = computed(() =>
            [...historyList.value].sort((a, b) => historyTimestampMs(b) - historyTimestampMs(a))
        );
        const timeRangeOptions = [
            { label: 'All time', value: 'all' },
            { label: 'Today', value: 'today' },
            { label: 'Last week', value: 'last_week' },
            { label: 'Last month', value: 'last_month' }
        ];

        const hasActiveFilters = computed(() =>
            Boolean(historySearchQuery.value.trim() || selectedTimeRange.value !== 'all')
        );
        const emptyHistoryMessage = computed(() =>
            historyList.value.length
                ? 'No history items match these filters.'
                : 'No history yet.'
        );

        const filteredHistoryList = computed(() => {
            const query = historySearchQuery.value.trim().toLowerCase();
            const timeRange = selectedTimeRange.value;

            return sortedHistoryList.value.filter(item => {
                const createdAt = historyTimestampMs(item);
                const matchesText = !query || payloadText(item.payload).toLowerCase().includes(query);
                return matchesText && matchesTimeRange(createdAt, timeRange);
            });
        });

        const rowsPerPage = ref(6);
        const currentPage = ref(1);
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredHistoryList.value.length / rowsPerPage.value)));
        const displayedRows = computed(() => {
            const start = (currentPage.value - 1) * rowsPerPage.value;
            return filteredHistoryList.value.slice(start, start + rowsPerPage.value);
        });
        const paginatedHistoryRows = computed(() =>
            displayedRows.value.map(item => ({
                item,
                rowKey: historyRowKey(item)
            }))
        );

        const historyPanel = ref(null);
        const tableContainer = ref(null);
        const paginationBar = ref(null);
        const defaultRowHeight = 72;

        function updateRowsPerPage() {
            if (!tableContainer.value) return;
            const containerHeight = tableContainer.value.clientHeight;
            const firstRow = tableContainer.value.querySelector('tbody tr');
            const measuredRowHeight = firstRow?.getBoundingClientRect().height || defaultRowHeight;
            const nextRowsPerPage = Math.max(1, Math.floor(containerHeight / measuredRowHeight));
            if (Number.isFinite(nextRowsPerPage) && nextRowsPerPage !== rowsPerPage.value) {
                rowsPerPage.value = nextRowsPerPage;
            }
            clampCurrentPage();
        }

        function nextPage() {
            if (currentPage.value < totalPages.value) currentPage.value += 1;
        }
        function prevPage() {
            if (currentPage.value > 1) currentPage.value -= 1;
        }

        function clampCurrentPage() {
            currentPage.value = Math.min(Math.max(currentPage.value, 1), totalPages.value);
        }

        async function getTTSHistory() {
            const resp = await getTTSRequest();
            const items = Array.isArray(resp)
                ? resp
                : resp.data || resp.requests || [];
            historyList.value = items.filter(item => item.status === 'finished');
        }

        function cleanAudioId(audioId) {
            return typeof audioId === 'string'
                ? audioId.replace(/^"|"$/g, '')
                : audioId;
        }

        function historyRowKey(item) {
            const rawKey = item?.tts_id
                ?? item?.ttsId
                ?? item?.upload_id
                ?? item?.uploadId
                ?? item?.audio_file_id
                ?? item?.audioFileId
                ?? item?.created_time
                ?? 'row';
            return String(rawKey);
        }

        function cleanupActiveAudio() {
            if (activeAudio) {
                activeAudio.pause();
                activeAudio.onended = null;
                activeAudio.onerror = null;
                activeAudio = null;
            }
            if (activeAudioUrl) {
                URL.revokeObjectURL(activeAudioUrl);
                activeAudioUrl = '';
            }
            playingAudioKey.value = null;
        }

        function isPlayLoading(rowKey) {
            return loadingPlayKey.value === rowKey;
        }

        function isPlayDisabled(rowKey) {
            return loadingPlayKey.value !== null && loadingPlayKey.value !== rowKey;
        }

        function isPlaying(rowKey) {
            return playingAudioKey.value === rowKey;
        }

        function isDownloadLoading(rowKey) {
            return loadingDownloadKey.value === rowKey;
        }

        function isDownloadDisabled(rowKey) {
            return loadingDownloadKey.value !== null && loadingDownloadKey.value !== rowKey;
        }

        function playButtonLabel(rowKey) {
            if (isPlayLoading(rowKey)) return 'Loading audio';
            if (isPlaying(rowKey)) return 'Stop audio';
            return 'Play audio';
        }

        async function playAudio(item, rowKey = historyRowKey(item)) {
            if (loadingPlayKey.value !== null) return;

            if (isPlaying(rowKey)) {
                playRequestId += 1;
                cleanupActiveAudio();
                return;
            }

            const currentRequestId = playRequestId + 1;
            playRequestId = currentRequestId;
            cleanupActiveAudio();
            loadingPlayKey.value = rowKey;
            try {
                const data = await getDownloadFileUrl(cleanAudioId(item.audio_file_id));
                if (currentRequestId !== playRequestId) return;

                const audio = new Audio(data.url);
                activeAudio = audio;
                activeAudioUrl = '';
                playingAudioKey.value = rowKey;
                audio.onended = cleanupActiveAudio;
                audio.onerror = cleanupActiveAudio;
                await audio.play();
            } catch (err) {
                console.error('Failed to download/play audio:', err);
                if (currentRequestId === playRequestId) {
                    cleanupActiveAudio();
                }
            } finally {
                if (loadingPlayKey.value === rowKey) {
                    loadingPlayKey.value = null;
                }
            }
        }

        async function downloadAudio(text, item, rowKey = historyRowKey(item)) {
            if (loadingDownloadKey.value !== null) return;
            loadingDownloadKey.value = rowKey;
            try {
                const data = await downloadFile(cleanAudioId(item.audio_file_id));
                const blob = new Blob([data], { type: 'audio/wav' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                const safeText = typeof text === 'string' ? text.slice(0, 50) : 'audio';
                link.download = safeText.trim() + '.wav';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            } catch (err) {
                console.error('Failed to download audio:', err);
            } finally {
                if (loadingDownloadKey.value === rowKey) {
                    loadingDownloadKey.value = null;
                }
            }
        }

        function payloadText(payload) {
            try {
                const obj = typeof payload === 'string'
                    ? JSON.parse(payload)
                    : payload || {};
                return obj.text || '';
            } catch {
                return '';
            }
        }

        function truncateText(text, maxLength = 10) {
            if (typeof text !== 'string') return '';
            return text.length > maxLength
                ? text.slice(0, maxLength) + '...'
                : text;
        }

        function historyTimestampMs(item) {
            const raw = item?.created_time
                ?? item?.createdTime
                ?? item?.created_at_time
                ?? item?.created_at
                ?? item?.createdAt;
            if (raw === null || raw === undefined || raw === '') return 0;
            if (typeof raw === 'number') {
                return raw > 1000000000000 ? raw : raw * 1000;
            }
            const numeric = Number(raw);
            if (Number.isFinite(numeric)) {
                return numeric > 1000000000000 ? numeric : numeric * 1000;
            }
            const parsed = Date.parse(raw);
            return Number.isNaN(parsed) ? 0 : parsed;
        }

        function matchesTimeRange(timestamp, range) {
            if (range === 'all') return true;
            if (!timestamp) return false;

            const now = Date.now();
            if (range === 'today') {
                const startOfToday = new Date();
                startOfToday.setHours(0, 0, 0, 0);
                return timestamp >= startOfToday.getTime() && timestamp <= now;
            }
            if (range === 'last_week') {
                return timestamp >= now - 7 * 24 * 60 * 60 * 1000 && timestamp <= now;
            }
            if (range === 'last_month') {
                return timestamp >= now - 30 * 24 * 60 * 60 * 1000 && timestamp <= now;
            }
            return true;
        }

        function formatHistoryTime(item) {
            const timestamp = historyTimestampMs(item);
            if (!timestamp) return '';
            return new Date(timestamp).toLocaleString();
        }

        function clearFilters() {
            historySearchQuery.value = '';
            selectedTimeRange.value = 'all';
        }

        function timeAgo(item) {
            const timestamp = historyTimestampMs(item);
            if (!timestamp) return '';
            const now = Date.now();
            const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));

            if (seconds < 60) return 'just now';
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
            const days = Math.floor(hours / 24);
            return `${days} day${days > 1 ? 's' : ''} ago`;
        }

        watch([historySearchQuery, selectedTimeRange], () => {
            currentPage.value = 1;
            cleanupActiveAudio();
            nextTick(updateRowsPerPage);
        });

        watch([filteredHistoryList, rowsPerPage], () => {
            clampCurrentPage();
            nextTick(updateRowsPerPage);
        });

        onMounted(async () => {
            await getTTSHistory();
            nextTick(() => {
                updateRowsPerPage();
                if (typeof ResizeObserver !== 'undefined' && tableContainer.value) {
                    observer = new ResizeObserver(updateRowsPerPage);
                    observer.observe(tableContainer.value);
                }
            });
        });

        onBeforeUnmount(() => {
            playRequestId += 1;
            cleanupActiveAudio();
            if (observer) observer.disconnect();
        });

        return {
            historyList,
            sortedHistoryList,
            filteredHistoryList,
            emptyHistoryMessage,
            playAudio,
            downloadAudio,
            historyRowKey,
            isPlayLoading,
            isPlayDisabled,
            isPlaying,
            isDownloadLoading,
            isDownloadDisabled,
            playButtonLabel,
            payloadText,
            truncateText,
            displayedRows,
            paginatedHistoryRows,
            currentPage,
            totalPages,
            nextPage,
            prevPage,
            historyPanel,
            tableContainer,
            paginationBar,
            historySearchQuery,
            selectedTimeRange,
            timeRangeOptions,
            hasActiveFilters,
            clearFilters,
            formatHistoryTime,
            timeAgo
        }
    }
}
</script>

<style scoped>
.history-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    background: transparent;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

.history-filters {
    flex: 0 0 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.5rem;
    padding: 0.25rem 0.25rem 0.75rem;
    border-bottom: 1px solid var(--border-color);
}

.history-filter-field {
    flex: 1 1 9rem;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    color: var(--text-secondary);
    font-size: 0.78rem;
    font-weight: 600;
}

.history-filter-search {
    flex-basis: 100%;
}

.history-filter-time {
    flex: 1 1 10rem;
}

.history-filter-field input,
.history-filter-field select {
    width: 100%;
    min-width: 0;
    height: 34px;
    box-sizing: border-box;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 0 0.6rem;
    color: var(--text-primary);
    background: var(--background-white);
    font-size: 0.85rem;
    font-weight: 500;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.history-filter-field select {
    cursor: pointer;
}

.history-filter-field input:focus,
.history-filter-field select:focus {
    border-color: var(--purple);
    box-shadow: 0 0 0 2px rgba(153, 0, 255, 0.14);
}

.clear-filter-button {
    flex: 0 0 auto;
    height: 34px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    padding: 0 0.75rem;
    background: #f5f5f7;
    color: var(--text-primary);
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.clear-filter-button:hover {
    background: #ececf1;
    border-color: #c9c9d3;
}

.history-results-bar {
    flex: 0 0 auto;
    min-height: 1.25rem;
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0 0.25rem;
    color: var(--text-secondary);
    font-size: 0.8rem;
}

.table-container {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
    padding: 0;
}

.pagination {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.25rem 0;
    border-top: 1px solid var(--border-color);
    font-size: 0.9rem;
}

.pagination button {
    min-width: 4.25rem;
    height: 34px;
    padding: 0 0.8rem;
    border: 1px solid var(--border-color);
    background: #f7f7f7;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.pagination button:hover:not(:disabled) {
    background: #ececf1;
    border-color: #c9c9d3;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    min-width: 6rem;
    text-align: center;
    color: var(--text-secondary);
    font-weight: 600;
}

.history-panel table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 0.95rem;
}

.history-panel thead {
    background-color: #f9f9f9;
}

.history-panel th {
    padding: 0.75rem 0rem;
    text-align: center;
}

.history-panel td {
    padding: 1rem 0rem;
    text-align: center;
    height: 72px;
    box-sizing: border-box;
}

.history-panel th {
    font-weight: 600;
    color: #333;
}

.history-panel tbody tr:hover {
    background-color: #f1f1f1;
}


.history-panel td:nth-child(1),
.history-panel th:nth-child(1) {
    width: 12%;
}

.history-panel td:nth-child(2),
.history-panel th:nth-child(2) {
    width: 44%;
}

.history-panel td:nth-child(3),
.history-panel th:nth-child(3) {
    width: 28%;
}

.history-panel td:nth-child(4),
.history-panel th:nth-child(4) {
    width: 16%;
}

.history-text-cell span {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.history-empty {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    text-align: center;
    color: var(--text-secondary);
    font-weight: 500;
}

.play-button,
.download-button {
    background: transparent;
    color: black;
    border: none;
    min-width: 32px;
    min-height: 32px;
    padding: 0.4rem;
    border-radius: 0.25rem;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.play-button:hover,
.download-button:hover,
.play-button.active {
    background: var(--purple);
    color: white;
}

.play-button:disabled,
.download-button:disabled {
    cursor: wait;
    opacity: 0.75;
}

.button-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
